/** Admin → Users: everyone across every organization. Still the documented
 *  N+1 over per-org endpoints; a real GET /admin/users replaces it when the
 *  org count makes the fan-out matter. */
import { useEffect, useState } from 'react';
import { adminApi, ROLE_LABEL, timeAgo, type CompanyUser } from '../../adminApi';
import { AdminTopCrumb } from '../../adminShell';

interface Row extends CompanyUser {
  companyName: string;
}

export function AdminUsers() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState('');

  function load() {
    return adminApi
      .companies()
      .then(async ({ companies }) => {
        const lists = await Promise.all(
          companies.map((c) =>
            adminApi
              .companyUsers(c.id)
              .then((r) => r.users.map((u) => ({ ...u, companyName: c.name })))
              .catch(() => [] as Row[]),
          ),
        );
        const all = lists.flat();
        all.sort((a, b) => a.companyName.localeCompare(b.companyName) || a.name.localeCompare(b.name));
        setRows(all);
      })
      .catch(() => setFailed(true));
  }

  useEffect(() => {
    void load();
  }, []);

  const all = rows ?? [];
  const q = filter.trim().toLowerCase();
  const shown = q ? all.filter((u) => `${u.name} ${u.email} ${u.companyName}`.toLowerCase().includes(q)) : all;

  const STATUS_TAG: Record<string, [string, string]> = {
    active: ['ok', 'Active'],
    invited: ['warn', 'Invited'],
    disabled: ['grey', 'Disabled'],
  };

  function setStatus(userId: string, status: string) {
    void adminApi.setUserStatus(userId, status).then(load);
  }

  return (
    <>
      <AdminTopCrumb>
        <b>Users</b>
      </AdminTopCrumb>
      <main className="page" id="main">
        <div className="page__head">
          <div>
            <h1>Users</h1>
            <p className="sub">Everyone across every organization</p>
          </div>
        </div>

        <div className="grid grid-4" style={{ marginBottom: 14 }}>
          <div className="kpi">
            <b>{rows ? all.length : '–'}</b>
            <span>Total users</span>
          </div>
          <div className="kpi">
            <b>{rows ? all.filter((u) => u.status === 'active').length : '–'}</b>
            <span>Active</span>
          </div>
          <div className="kpi kpi--alert">
            <b>{rows ? all.filter((u) => u.status === 'invited').length : '–'}</b>
            <span>Pending invites</span>
          </div>
          <div className="kpi">
            <b>{rows ? all.filter((u) => u.status === 'disabled').length : '–'}</b>
            <span>Disabled</span>
          </div>
        </div>

        <div className="panel">
          <div className="panel__head">
            <h2>All users</h2>
            <div className="spacer" />
            <span className="tag tag--grey">{rows ? `${shown.length} shown` : '–'}</span>
          </div>
          <div className="panel__body" style={{ paddingBottom: 0 }}>
            <div className="field" style={{ maxWidth: 320, marginBottom: 12 }}>
              <label htmlFor="filter">Filter</label>
              <input
                id="filter"
                type="search"
                placeholder="Name, email or organization"
                autoComplete="off"
                spellCheck={false}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
          </div>
          <div className="table-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Organization</th>
                  <th>Role</th>
                  <th>Last active</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {failed && (
                  <tr>
                    <td colSpan={7} className="dim" style={{ padding: '22px 13px' }}>
                      Could not load users.
                    </td>
                  </tr>
                )}
                {rows && shown.length === 0 && (
                  <tr>
                    <td colSpan={7} className="dim" style={{ padding: '22px 13px' }}>
                      No users match.
                    </td>
                  </tr>
                )}
                {shown.map((u) => {
                  const tag = STATUS_TAG[u.status] ?? ['grey', u.status];
                  return (
                    <tr key={u.id}>
                      <td className="strong">{u.name}</td>
                      <td className="dim">{u.email}</td>
                      <td>{u.companyName}</td>
                      <td>{ROLE_LABEL[u.role] ?? u.role}</td>
                      <td className="dim">{timeAgo(u.lastActiveAt)}</td>
                      <td>
                        <span className={`tag tag--${tag[0]}`}>{tag[1]}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {u.status === 'active' ? (
                          <button className="btn btn--sm btn--danger" onClick={() => setStatus(u.id, 'disabled')}>
                            Disable
                          </button>
                        ) : (
                          <button className="btn btn--sm" onClick={() => setStatus(u.id, 'active')}>
                            {u.status === 'invited' ? 'Activate' : 'Re-enable'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="panel__foot">Status changes here are written to the organization’s own audit log.</div>
        </div>
      </main>
    </>
  );
}
